import type {
  AttachBusinessQuotePayload,
  BusinessRisk,
} from '@/services/quotes/types';
import { ValidationError } from '@/shared/exceptions/validation.error';
import { toaster } from '@/shared/ui/toaster.component';
import { Button } from '@noorden-insurtech/alba-design-system';
import { formOptions } from '@tanstack/react-form';
import { ArrowLeft, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useQuoteParams } from '../../context/quote-params.context';
import { useQuoteSummary } from '../../context/quote-summary.context';
import { useCopyInsuredObject } from '../../hooks/business/use-copy-insured-object.hook';
import { useDeleteInsuredObject } from '../../hooks/business/use-delete-insured-object.hook';
import { useUpdateBusinessQuote } from '../../hooks/business/use-update-quote.hook';
import { withForm } from '../../hooks/common/use-quote-form.hook';
import { useConstructionTypes } from '../../hooks/homeowner/use-construction-types.hook';
import { useInsuredObjects } from '../../hooks/homeowner/use-insured-objects.hook';
import { AddBusinessAddressModal } from './add-business-address-modal.component';
import { type BusinessLocation } from './business-location';
import { BusinessLocationCard } from './business-location-card.component';
import { PropertyDeniedDialog } from './property-denied.dialog';
import { findDuplicateBusinessLocationIds } from '../../utils/find-duplicate-business-location-ids.util';
import { mapBusinessRisksToLocations } from '../../utils/map-business-risks-to-locations.util';

const formOpts = formOptions({
  defaultValues: {
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    activityType: '',
    constructionType: '',
    isLandmarked: false,
    insuredObjectType: '',
    riskValue: '',
  },
});

function mapRisksToLocations(risks: BusinessRisk[]): BusinessLocation[] {
  return mapBusinessRisksToLocations(risks);
}

export const StepRiskBusinessData = withForm({
  ...formOpts,
  props: {
    onNext: () => {},
    onBack: () => {},
    stepCode: '',
    isViewMode: false,
    initialRisks: [] as BusinessRisk[],
    /**
     * Quando true, alterações em locais ficam apenas no state local — não
     * dispara `updateBusinessQuote` (attachElement) nem copy/delete remotos.
     * Usado no fluxo de análise onde todas as mudanças devem ser enviadas
     * juntas no botão final via calculate-analysis.
     */
    skipBackendSubmit: false,
    /** Notifica o componente pai a cada mudança no array de locais. */
    onLocationsChange: undefined as
      | ((_next: BusinessLocation[]) => void)
      | undefined,
  },
  render: ({
    onNext,
    onBack,
    isViewMode,
    initialRisks,
    stepCode,
    skipBackendSubmit,
    onLocationsChange,
  }) => {
    const { id, productId } = useQuoteParams();
    const { updateProperty } = useQuoteSummary();
    const {
      mutateAsync: updateQuoteAsync,
      isPending: isUpdating,
      isError: isUpdateError,
      error: updateError,
    } = useUpdateBusinessQuote();
    const { mutateAsync: copyInsuredObjectAsync, isPending: isCopying } =
      useCopyInsuredObject();
    const { mutateAsync: deleteInsuredObjectAsync, isPending: isDeleting } =
      useDeleteInsuredObject();
    const { data: constructionTypes } = useConstructionTypes();
    const { data: insuredObjectTypes } = useInsuredObjects();
    const [businessLocations, setBusinessLocations] = useState<
      BusinessLocation[]
    >([]);
    const [expandedLocationIds, setExpandedLocationIds] = useState<string[]>(
      []
    );
    const [duplicateLocationIds, setDuplicateLocationIds] = useState<string[]>(
      []
    );

    // Após a primeira tentativa de avanço bloqueada, reavalia a duplicidade a
    // cada mudança na lista — o destaque some assim que o usuário corrige.
    useEffect(() => {
      setDuplicateLocationIds(current =>
        current.length === 0
          ? current
          : findDuplicateBusinessLocationIds(businessLocations)
      );
    }, [businessLocations]);

    useEffect(() => {
      if (!initialRisks || initialRisks.length === 0) return;
      const locations = mapRisksToLocations(initialRisks);
      setBusinessLocations(locations);
    }, [initialRisks]);

    useEffect(() => {
      onLocationsChange?.(businessLocations);
    }, [businessLocations, onLocationsChange]);

    const insuredObjectOptions = useMemo(
      () =>
        (insuredObjectTypes ?? []).map(item => ({
          value: String(item.code ?? ''),
          label: item.description,
        })),
      [insuredObjectTypes]
    );
    const constructionTypeOptions = useMemo(
      () =>
        (constructionTypes ?? []).map(type => ({
          value: String(type.code ?? ''),
          label: type.description,
        })),
      [constructionTypes]
    );

    const totalLocations = businessLocations.length;
    const allLocationIds = useMemo(
      () => businessLocations.map(item => item.id),
      [businessLocations]
    );
    const areAllLocationsExpanded =
      allLocationIds.length > 0 &&
      allLocationIds.every(id => expandedLocationIds.includes(id));

    const toggleLocation = (locationId: string) => {
      setExpandedLocationIds(current =>
        current.includes(locationId)
          ? current.filter(id => id !== locationId)
          : [...current, locationId]
      );
    };

    const toggleAllLocations = () => {
      setExpandedLocationIds(areAllLocationsExpanded ? [] : allLocationIds);
    };

    const parseCurrencyToNumber = (currencyString: string): number => {
      if (!currencyString || !currencyString.trim()) return 0;
      const cleaned = currencyString
        .replace(/R\$\s*/g, '')
        .replace(/\./g, '')
        .replace(',', '.')
        .trim();
      return parseFloat(cleaned) || 0;
    };

    const buildAttachPayload = (
      location: BusinessLocation
    ): Partial<AttachBusinessQuotePayload> => {
      const [fireAnswer, upperFloorAnswer, insideMallAnswer, theftAnswer] =
        location.questionnaireAnswers ?? [];

      const toArray = (answer: string | string[] | undefined): string[] => {
        if (Array.isArray(answer)) return answer.filter(Boolean);
        if (answer) return [answer];
        return [];
      };
      const toString = (answer: string | string[] | undefined): string => {
        if (Array.isArray(answer)) return answer[0] ?? '';
        return answer ?? '';
      };

      const businessInterruptionAmount = parseCurrencyToNumber(
        location.businessInterruptionValue ?? ''
      );

      return {
        step: stepCode || 'RISK_BUSINESS',
        insuredObject: {
          address: location.street,
          addressComplement: location.complement,
          addressNumber: location.number,
          addressType: 1,
          city: location.city,
          state: location.state,
          constructionType: location.constructionType,
          countryCode: 'BR',
          latitude: '',
          longitude: '',
          listedPropertyFlag: location.isLandmarked ? 'Y' : 'N',
          neighborhood: location.neighborhood,
          propertyClassification: location.propertyClassification ?? '1',
          street: location.street,
          postCode: location.cep.replace(/\D/g, ''),
          propertyType: location.propertyType ?? '1',
          activityTypePerformed: location.activityType,
          scheduleExt: [
            {
              insuredObject: location.insuredObjectType,
              value: parseCurrencyToNumber(location.riskValue),
            },
          ],
          ...(businessInterruptionAmount > 0
            ? { businessInterruptionAmount }
            : {}),
          ...(location.underwritingDiscountRequestInd
            ? {
                underwritingDiscountRequestInd:
                  location.underwritingDiscountRequestInd,
              }
            : {}),
          ...(location.riskPk !== undefined ? { pk: location.riskPk } : {}),
        },
        riskQuestionary: {
          fireProtectionSystems: toArray(fireAnswer),
          isUpperFloor: toString(upperFloorAnswer),
          isInsideMall: toString(insideMallAnswer),
          securityAlarmSystems: toArray(theftAnswer),
        },
        ...(location.coveragesPayload && location.coveragesPayload.length > 0
          ? { coverages: location.coveragesPayload }
          : {}),
        ...(location.benefitsPayload && location.benefitsPayload.length > 0
          ? { benefits: location.benefitsPayload }
          : {}),
      };
    };

    const syncLocationsFromResponse = (responseData: any) => {
      const risks: BusinessRisk[] = responseData?.lobs?.[0]?.risks ?? [];
      if (risks.length > 0) {
        const updatedLocations = mapRisksToLocations(risks);
        setBusinessLocations(updatedLocations);
        setExpandedLocationIds(updatedLocations.map(l => l.id));
      }
    };

    const handleAddBusinessLocation = async (location: BusinessLocation) => {
      if (skipBackendSubmit) {
        const newLocation: BusinessLocation = {
          ...location,
          id: location.id || `local-${Date.now()}`,
        };
        setBusinessLocations(prev => [...prev, newLocation]);
        setExpandedLocationIds(prev => [...prev, newLocation.id]);
        return;
      }

      if (!id) throw new Error('ID da cotação não encontrado');

      const result = await updateQuoteAsync({
        id,
        payload: buildAttachPayload(location),
      });

      if (!result.success) {
        throw new Error(
          result.error || 'Não foi possível salvar o local de risco.'
        );
      }

      syncLocationsFromResponse(result.data);
    };

    const handleUpdateBusinessLocation = async (
      updatedLocation: BusinessLocation
    ) => {
      if (skipBackendSubmit) {
        setBusinessLocations(prev =>
          prev.map(loc =>
            loc.id === updatedLocation.id ? updatedLocation : loc
          )
        );
        return;
      }

      if (!id) throw new Error('ID da cotação não encontrado');

      const result = await updateQuoteAsync({
        id,
        payload: buildAttachPayload(updatedLocation),
      });

      if (!result.success) {
        throw new Error(
          result.error || 'Não foi possível atualizar o local de risco.'
        );
      }

      syncLocationsFromResponse(result.data);
    };

    const handleCopyBusinessLocation = async (riskPk: number) => {
      if (skipBackendSubmit) {
        const source = businessLocations.find(loc => loc.riskPk === riskPk);
        if (!source) return;
        const copy: BusinessLocation = {
          ...source,
          id: `local-${Date.now()}`,
          riskPk: undefined,
        };
        setBusinessLocations(prev => [...prev, copy]);
        setExpandedLocationIds(prev => [...prev, copy.id]);
        toaster.create({
          type: 'success',
          title: 'Local copiado',
          description:
            'O local foi copiado localmente. As mudanças serão enviadas no botão Enviar Cálculo.',
        });
        return;
      }

      if (!id) return;

      try {
        const result = await copyInsuredObjectAsync({ policyId: id, riskPk });

        if (!result.success || !result.data) {
          toaster.create({
            type: 'error',
            title: 'Erro ao copiar local',
            description:
              result.error || 'Não foi possível copiar o local de risco.',
          });
          return;
        }

        const risks = result.data.lobs?.[0]?.risks ?? [];
        const updatedLocations = mapRisksToLocations(risks);
        setBusinessLocations(updatedLocations);
        setExpandedLocationIds(updatedLocations.map(l => l.id));

        toaster.create({
          type: 'success',
          title: 'Local copiado',
          description: 'O local de risco foi copiado com sucesso.',
        });
      } catch {
        toaster.create({
          type: 'error',
          title: 'Erro ao copiar local',
          description: 'Não foi possível copiar o local de risco.',
        });
      }
    };

    const handleDeleteBusinessLocation = async (riskPk: number) => {
      if (skipBackendSubmit) {
        const removedIds = businessLocations
          .filter(loc => loc.riskPk === riskPk)
          .map(loc => loc.id);
        setBusinessLocations(prev => prev.filter(loc => loc.riskPk !== riskPk));
        setExpandedLocationIds(prev =>
          prev.filter(expandedId => !removedIds.includes(expandedId))
        );
        toaster.create({
          type: 'success',
          title: 'Local removido',
          description:
            'O local foi removido localmente. As mudanças serão enviadas no botão Enviar Cálculo.',
        });
        return;
      }

      if (!id) return;

      try {
        const result = await deleteInsuredObjectAsync({
          policyId: id,
          riskPk,
        });

        if (!result.success) {
          toaster.create({
            type: 'error',
            title: 'Erro ao remover local',
            description:
              result.error || 'Não foi possível remover o local de risco.',
          });
          return;
        }

        // Atualização otimista: garante que o local removido sai do estado
        // local independente do shape da resposta do backend.
        const removedLocationIds = businessLocations
          .filter(location => location.riskPk === riskPk)
          .map(location => location.id);

        setBusinessLocations(prev =>
          prev.filter(location => location.riskPk !== riskPk)
        );
        setExpandedLocationIds(prev =>
          prev.filter(expandedId => !removedLocationIds.includes(expandedId))
        );

        // Se a API devolveu a lista autoritativa de riscos, prioriza ela.
        const risksFromResponse = result.data?.lobs?.[0]?.risks;
        if (Array.isArray(risksFromResponse)) {
          const updatedLocations = mapRisksToLocations(risksFromResponse);
          setBusinessLocations(updatedLocations);
          setExpandedLocationIds(updatedLocations.map(l => l.id));
        }

        toaster.create({
          type: 'success',
          title: 'Local removido',
          description: 'O local de risco foi removido com sucesso.',
        });
      } catch {
        toaster.create({
          type: 'error',
          title: 'Erro ao remover local',
          description: 'Não foi possível remover o local de risco.',
        });
      }
    };

    const handleSubmit = () => {
      if (businessLocations.length === 0) {
        toaster.create({
          type: 'error',
          title: 'Nenhum local cadastrado',
          description: 'Adicione pelo menos um local de risco para avançar.',
        });
        return;
      }

      const duplicateIds = findDuplicateBusinessLocationIds(businessLocations);
      setDuplicateLocationIds(duplicateIds);
      if (duplicateIds.length > 0) {
        toaster.create({
          type: 'error',
          title: 'Endereços duplicados',
          description:
            'Existem locais de risco com o mesmo endereço e complemento. Diferencie os locais destacados pelo complemento para avançar.',
        });
        return;
      }

      const firstLocation = businessLocations[0];
      updateProperty({
        cep: firstLocation.cep,
        address: firstLocation.street,
        number: firstLocation.number,
        complement: firstLocation.complement,
        neighborhood: firstLocation.neighborhood,
        city: firstLocation.city,
        state: firstLocation.state,
        constructionType: firstLocation.constructionType,
        insuredObjectType: firstLocation.insuredObjectType,
      });

      onNext?.();
    };

    return (
      <div className='space-y-8'>
        <div className='space-y-6'>
          <div className='flex flex-col gap-4 md:flex-row md:items-center md:justify-between md:gap-6'>
            <div className='space-y-2'>
              <h2 className='text-base font-normal text-alba-black-500'>
                Dados do Local
              </h2>
              <div className='space-y-1'>
                <p className='text-sm font-bold text-alba-black-500'>
                  Adicione e gerencie locais
                </p>
                <p className='text-sm text-alba-gray-600'>
                  Cadastre os locais vinculados ao seguro e organize coberturas,
                  questionários e informações de cada local.
                </p>
              </div>
            </div>

            <AddBusinessAddressModal
              onConfirm={handleAddBusinessLocation}
              constructionTypeOptions={constructionTypeOptions}
              insuredObjectOptions={insuredObjectOptions}
              isLoading={isUpdating}
              skipCoverageValueValidation={skipBackendSubmit}
            />
          </div>

          {businessLocations.length > 0 && (
            <>
              <div className='flex flex-col gap-1 md:flex-row md:items-center md:justify-between md:gap-4'>
                <h3 className='text-base font-normal text-alba-black-500'>
                  Lista de Locais
                </h3>
                <button
                  type='button'
                  onClick={toggleAllLocations}
                  className='inline-flex items-center gap-2 self-start rounded-lg py-1 text-xs font-medium text-alba-blue-500 md:px-4 md:py-2'
                >
                  {areAllLocationsExpanded
                    ? `Fechar Todos (${totalLocations})`
                    : `Abrir Todos (${totalLocations})`}
                  {areAllLocationsExpanded ? (
                    <ChevronUp className='h-4 w-4' strokeWidth={1.6} />
                  ) : (
                    <ChevronDown className='h-4 w-4' strokeWidth={1.6} />
                  )}
                </button>
              </div>

              <div className='space-y-4'>
                {businessLocations.map((location, index) => (
                  <BusinessLocationCard
                    key={location.id}
                    location={location}
                    locationNumber={index + 1}
                    isOpen={expandedLocationIds.includes(location.id)}
                    hasError={duplicateLocationIds.includes(location.id)}
                    canEdit={!isViewMode}
                    isCopying={isCopying}
                    isDeleting={isDeleting}
                    onToggleLocation={() => toggleLocation(location.id)}
                    onDeleteBusinessLocation={handleDeleteBusinessLocation}
                    onUpdateBusinessLocation={handleUpdateBusinessLocation}
                    onCopyBusinessLocation={handleCopyBusinessLocation}
                    constructionTypeOptions={constructionTypeOptions}
                    insuredObjectOptions={insuredObjectOptions}
                    productId={productId}
                    skipCoverageValueValidation={skipBackendSubmit}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className='flex flex-row items-center justify-between gap-3 self-stretch sm:gap-6 sm:px-6'>
          <Button
            variant='ghost'
            size='md'
            className='flex h-12 min-w-0 items-center justify-start gap-2 px-0 sm:h-auto'
            onClick={onBack}
          >
            <ArrowLeft className='w-5 h-5' strokeWidth={1.6} />
            Voltar
          </Button>

          <Button
            variant='primary'
            size='md'
            className='flex h-12 min-w-[132px] items-center justify-center gap-2 rounded-xl px-5 py-2 sm:h-auto sm:min-w-[148px] sm:px-6'
            onClick={handleSubmit}
            loading={isUpdating || isCopying || isDeleting}
            disabled={isUpdating || isCopying || isDeleting}
          >
            Próximo
            <ArrowRight className='w-6 h-6' strokeWidth={2} />
          </Button>
        </div>

        <PropertyDeniedDialog
          isOpen={
            isUpdateError &&
            updateError instanceof ValidationError &&
            updateError.fieldErrors?.quoteStatus?.code === '2'
          }
          detail={
            updateError instanceof ValidationError
              ? updateError.detail
              : undefined
          }
          errors={
            updateError instanceof ValidationError ? updateError.messages : []
          }
        />
      </div>
    );
  },
});
